import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  LogOut, Calendar as CalIcon, Mail, MailOpen, Phone, MapPin, ChevronDown, ChevronUp,
  Trash2, X as XIcon, CheckCircle2, Clock, XCircle, PackageCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { authApi, clearToken, getToken } from "@/lib/adminApi";

const STATUS_META = {
  pending:   { label: "En attente", color: "bg-amber-100 text-amber-800",  Icon: Clock },
  confirmed: { label: "Confirmée",  color: "bg-blue-100 text-blue-800",    Icon: CheckCircle2 },
  completed: { label: "Complétée",  color: "bg-emerald-100 text-emerald-800", Icon: PackageCheck },
  cancelled: { label: "Annulée",    color: "bg-red-100 text-red-800",      Icon: XCircle },
};

const FREQ_LABELS = {
  une_fois: "Lavage unique (1 fois)",
  mensuel: "Lavage mensuel",
  bimensuel: "Lavage bimensuel (2 fois/mois)",
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!getToken()) { navigate("/admin"); return; }
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        authApi.get("/admin/bookings"),
        authApi.get("/admin/contacts"),
      ]);
      setBookings(b.data);
      setContacts(c.data);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin");
  };

  const updateStatus = async (id, status) => {
    try {
      await authApi.patch(`/admin/bookings/${id}`, { status });
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Réservation ${STATUS_META[status].label.toLowerCase()}`);
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  const deleteBooking = async (id) => {
    try {
      await authApi.delete(`/admin/bookings/${id}`);
      setBookings((list) => list.filter((b) => b.id !== id));
      toast.success("Réservation supprimée");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const setBookingRead = async (id, read) => {
    setBookings((list) => list.map((b) => (b.id === id ? { ...b, read } : b)));
    try {
      await authApi.patch(`/admin/bookings/${id}/read`, { read });
    } catch {
      toast.error("Échec de la mise à jour");
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, read: !read } : b)));
    }
  };

  const setContactRead = async (id, read) => {
    setContacts((list) => list.map((c) => (c.id === id ? { ...c, read } : c)));
    try {
      await authApi.patch(`/admin/contacts/${id}/read`, { read });
    } catch {
      toast.error("Échec de la mise à jour");
      setContacts((list) => list.map((c) => (c.id === id ? { ...c, read: !read } : c)));
    }
  };

  const toggleBooking = (b) => {
    const open = expanded === b.id;
    setExpanded(open ? null : b.id);
    if (!open && !b.read) setBookingRead(b.id, true);
  };

  const deleteContact = async (id) => {
    try {
      await authApi.delete(`/admin/contacts/${id}`);
      setContacts((list) => list.filter((c) => c.id !== id));
      toast.success("Message supprimé");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const filtered = statusFilter === "all" ? bookings : bookings.filter((b) => b.status === statusFilter);

  const unreadBookings = bookings.filter((b) => !b.read).length;
  const unreadContacts = contacts.filter((c) => !c.read).length;

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_exterior-clean-pro-1/artifacts/11ya2fow_Lavage.png"
              alt="Pro-Jet Nettoyage"
              className="h-10 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <div className="font-semibold text-secondary text-sm">Tableau de bord</div>
              <div className="text-xs text-muted-foreground">Administrateur</div>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} data-testid="admin-logout" className="text-secondary">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total", value: stats.total, color: "text-secondary" },
            { label: "En attente", value: stats.pending, color: "text-amber-600" },
            { label: "Confirmées", value: stats.confirmed, color: "text-primary" },
            { label: "Annulées", value: stats.cancelled, color: "text-red-500" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</div>
              <div className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              Réservations ({bookings.length})
              {unreadBookings > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] font-semibold" data-testid="tab-bookings-unread">{unreadBookings}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              Messages ({contacts.length})
              {unreadContacts > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] font-semibold" data-testid="tab-contacts-unread">{unreadContacts}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-muted-foreground">Filtrer par statut</div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-white" data-testid="admin-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmées</SelectItem>
                  <SelectItem value="completed">Complétées</SelectItem>
                  <SelectItem value="cancelled">Annulées</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-16 text-muted-foreground">Chargement...</div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-100" data-testid="admin-bookings-empty">
                <CalIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Aucune réservation.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((b) => {
                  const isOpen = expanded === b.id;
                  const meta = STATUS_META[b.status] || STATUS_META.pending;
                  const Icon = meta.Icon;
                  return (
                    <div
                      key={b.id}
                      className={`bg-white rounded-2xl border overflow-hidden transition-colors ${
                        b.read ? "border-gray-100" : "border-primary/40 bg-primary/[0.03]"
                      }`}
                      data-testid={`booking-row-${b.id}`}
                    >
                      <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => toggleBooking(b)}
                        data-testid={`booking-toggle-${b.id}`}
                        className="flex-1 min-w-0 flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 ${meta.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            {!b.read && (
                              <span className="w-2 h-2 rounded-full bg-primary" data-testid={`booking-unread-dot-${b.id}`} />
                            )}
                            <span className={`text-secondary ${b.read ? "font-medium" : "font-bold"}`}>{b.first_name} {b.last_name}</span>
                            <Badge className={`${meta.color} border-0 font-medium`}>{meta.label}</Badge>
                            {!b.read && (
                              <Badge className="bg-primary text-white border-0 font-medium">Non lue</Badge>
                            )}
                            {b.promo_code && (
                              <Badge className="bg-emerald-100 text-emerald-800 border-0 font-medium" data-testid={`booking-promo-badge-${b.id}`}>
                                {b.promo_code}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1 truncate">
                            {b.service_type} • {b.date} à {b.time_slot} • {b.city}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground hidden sm:block">
                          #{b.id.slice(0, 8).toUpperCase()}
                        </div>
                        {isOpen ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setBookingRead(b.id, !b.read)}
                        data-testid={`booking-read-toggle-${b.id}`}
                        title={b.read ? "Marquer comme non lue" : "Marquer comme lue"}
                        className="mr-4 text-muted-foreground hover:text-primary"
                      >
                        {b.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                      </Button>
                      </div>

                      {isOpen && (
                        <div className="border-t border-gray-100 p-5 bg-gray-50/50 space-y-6" data-testid={`booking-details-${b.id}`}>
                          {/* Contact */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div><span className="text-muted-foreground">Téléphone:</span> <a href={`tel:${b.phone}`} className="text-primary font-medium block">{b.phone}</a></div>
                            <div><span className="text-muted-foreground">Courriel:</span> <a href={`mailto:${b.email}`} className="text-primary font-medium block break-all">{b.email}</a></div>
                            <div><span className="text-muted-foreground">Adresse:</span> <span className="block font-medium text-secondary">{b.address}, {b.city} {b.postal_code}</span></div>
                          </div>

                          {/* Service details */}
                          <div className="text-sm" data-testid={`booking-promo-${b.id}`}>
                            <span className="text-muted-foreground">Code promotionnel : </span>
                            {b.promo_code ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-0 font-medium ml-1">
                                {b.promo_code} (-{b.promo_discount || 0} $)
                              </Badge>
                            ) : (
                              <span className="font-medium text-secondary">Aucun</span>
                            )}
                          </div>

                          {b.bin_count || (b.bin_types && b.bin_types.length > 0) ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid={`booking-bins-details-${b.id}`}>
                              <div><span className="text-muted-foreground">Nombre de bacs:</span> <span className="block font-medium">{b.bin_count || "—"}</span></div>
                              <div><span className="text-muted-foreground">Types:</span> <span className="block font-medium capitalize">{b.bin_types?.join(", ") || "—"}</span></div>
                              <div><span className="text-muted-foreground">Fréquence:</span> <span className="block font-medium">{FREQ_LABELS[b.frequency] || "—"}</span></div>
                              <div><span className="text-muted-foreground">Créée:</span> <span className="block font-medium">{format(new Date(b.created_at), "d MMM yyyy", { locale: fr })}</span></div>
                            </div>
                          ) : (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div><span className="text-muted-foreground">Surface:</span> <span className="block font-medium">{b.surface || "—"}</span></div>
                            <div><span className="text-muted-foreground">Saleté:</span> <span className="block font-medium">{b.dirt_level || "—"}</span></div>
                            <div><span className="text-muted-foreground">Huile/Rouille:</span> <span className="block font-medium">{b.oil_or_rust || "—"}</span></div>
                            <div><span className="text-muted-foreground">Créée:</span> <span className="block font-medium">{format(new Date(b.created_at), "d MMM yyyy", { locale: fr })}</span></div>
                          </div>
                          )}

                          {b.comments && (
                            <div className="text-sm">
                              <div className="text-muted-foreground mb-1">Commentaires:</div>
                              <div className="bg-white rounded-xl p-3 border border-gray-100">{b.comments}</div>
                            </div>
                          )}

                          {/* Photos */}
                          {b.photos && b.photos.length > 0 && (
                            <div>
                              <div className="text-sm text-muted-foreground mb-2">Photos ({b.photos.length})</div>
                              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                {b.photos.map((p, i) => (
                                  <a key={i} href={p} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                                    <img src={p} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                            <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                              <SelectTrigger className="w-44 bg-white" data-testid={`admin-status-select-${b.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">En attente</SelectItem>
                                <SelectItem value="confirmed">Confirmée</SelectItem>
                                <SelectItem value="completed">Complétée</SelectItem>
                                <SelectItem value="cancelled">Annulée</SelectItem>
                              </SelectContent>
                            </Select>

                            {b.status !== "cancelled" && (
                              <Button
                                variant="outline"
                                onClick={() => updateStatus(b.id, "cancelled")}
                                data-testid={`admin-cancel-${b.id}`}
                                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
                              >
                                <XIcon className="w-4 h-4 mr-2" /> Annuler
                              </Button>
                            )}

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  data-testid={`admin-delete-${b.id}`}
                                  className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl ml-auto"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer cette réservation ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Cette action est irréversible.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteBooking(b.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid={`admin-delete-confirm-${b.id}`}
                                  >
                                    Supprimer
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            {loading ? (
              <div className="text-center py-16 text-muted-foreground">Chargement...</div>
            ) : contacts.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-100" data-testid="admin-contacts-empty">
                <Mail className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun message.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div key={c.id} className={`rounded-2xl p-5 border transition-colors ${c.read ? "bg-white border-gray-100" : "bg-primary/[0.03] border-primary/40"}`} data-testid={`contact-row-${c.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          {!c.read && <span className="w-2 h-2 rounded-full bg-primary" data-testid={`contact-unread-dot-${c.id}`} />}
                          <span className={`text-secondary ${c.read ? "font-medium" : "font-bold"}`}>{c.name}</span>
                          {!c.read && <Badge className="bg-primary text-white border-0 font-medium">Non lu</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-4 mt-1 text-sm">
                          <a href={`mailto:${c.email}`} className="text-primary flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</a>
                          {c.phone && <a href={`tel:${c.phone}`} className="text-primary flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</a>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(c.created_at), "d MMM yyyy", { locale: fr })}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setContactRead(c.id, !c.read)}
                          data-testid={`contact-read-toggle-${c.id}`}
                          title={c.read ? "Marquer comme non lu" : "Marquer comme lu"}
                          className="text-muted-foreground hover:text-primary"
                        >
                          {c.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`contact-delete-${c.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Le message de {c.name} sera définitivement supprimé.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteContact(c.id)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid={`contact-delete-confirm-${c.id}`}
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-secondary leading-relaxed whitespace-pre-wrap">{c.message}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
